import { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../config';
import { mockAuthHeaders } from '../utils/mockAuth';

/**
 * Hook for polling background task status.
 *
 * Usage:
 *   const { task, startTask, isRunning, isComplete, isFailed, result, error } = useTaskPolling({
 *     onComplete: (result) => { ... },
 *     onError: (error) => { ... },
 *     interval: 2000,
 *   });
 *
 *   // To start polling after an API call returns a task_id:
 *   const res = await fetch('/api/automation/import-from-url', ...);
 *   const { task_id } = await res.json();
 *   startTask(task_id);
 */

export default function useTaskPolling({
  onComplete,
  onError,
  interval = 2000,
} = {}) {
  const [taskId, setTaskId] = useState(null);
  const [task, setTask] = useState(null);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);

  // Keep refs current without triggering re-renders
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const startTask = useCallback((id) => {
    setTaskId(id);
    setTask({ task_id: id, status: 'pending', progress: null });
  }, []);

  const reset = useCallback(() => {
    setTaskId(null);
    setTask(null);
  }, []);

  useEffect(() => {
    if (!taskId) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/tasks/${taskId}`, {
          headers: { ...mockAuthHeaders() },
          credentials: 'include',
        });
        if (!res.ok) return; // task might not be written yet, retry
        const data = await res.json();
        if (cancelled) return;

        setTask(data);

        if (data.status === 'completed') {
          onCompleteRef.current?.(data.result);
          return; // stop polling
        }
        if (data.status === 'failed') {
          onErrorRef.current?.(data.error);
          return; // stop polling
        }

        // Still running — schedule next poll
        timerId = setTimeout(poll, interval);
      } catch (err) {
        if (!cancelled) {
          // Network error — keep trying
          timerId = setTimeout(poll, interval * 2);
        }
      }
    };

    let timerId = setTimeout(poll, 500); // first poll after 500ms

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [taskId, interval]);

  return {
    task,
    taskId,
    startTask,
    reset,
    isRunning: task?.status === 'pending' || task?.status === 'running',
    isComplete: task?.status === 'completed',
    isFailed: task?.status === 'failed',
    result: task?.result,
    error: task?.error,
    progress: task?.progress,
  };
}
